import { NgClass } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    OnInit,
    Signal,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { Roles, SupportedLanguages } from '@shs-digi-care-monorepo/interfaces';
import packageJson from 'package.json';
import { filter, map, Observable, tap } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { IUser } from '../../interfaces/iUser';
import { ApplicationState } from '../../../store/application/settings.state';
import { UsersState } from '../../../store/user';
import { AppsyncService } from '../../services/appsync.service';
import { ThemeService } from '../../services/theme.service';
import { ScopeState } from '../../states/scope/scope.state';
import { MDLA_SCOPE_ENUM } from '../../tokens/mdla-scope.token';
import { HeaderMenuButtonsComponent } from './header-menu-buttons/header-menu-buttons.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header-notifications.scss'],
    imports: [RouterLink, NgClass, HeaderMenuButtonsComponent]
})
export class HeaderComponent implements OnInit, AfterViewInit {
    private translate = inject(TranslateService);
    private themeService = inject(ThemeService);
    private auth = inject(AuthService);
    protected router = inject(Router);
    private store = inject(Store);
    private appSyncService = inject(AppsyncService);
    private destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);

    @ViewChild('headerBar', { static: true }) private readonly headerBar!: ElementRef<HTMLElement>;

    protected version = packageJson.version;
    protected me$: Observable<IUser> = inject(Store)
        .select(UsersState.getMe())
        .pipe(filter((me) => !!me));
    protected isTaskOwnerOrAdmin: WritableSignal<boolean> = signal<boolean>(false);
    protected mdlaScope: Signal<MDLA_SCOPE_ENUM> = select(ScopeState.scope);
    protected isMdlaScopeSelectable: Signal<boolean> = computed(() => {
        return this.currentUrl().startsWith('/admin/rules');
    });
    protected supportedLanguages: WritableSignal<string[]> = signal<string[]>([]);

    public readonly showEnvironmentHint: Signal<boolean> = this.store.selectSignal(
        ApplicationState.getShowEnvironmentHint
    );

    protected scopeRoute: Signal<string | null> = computed(() => {
        const scope = this.mdlaScope();
        return scope === MDLA_SCOPE_ENUM.NAM ? null : scope?.toLowerCase() || null;
    });

    protected isNam: Signal<boolean> = computed(() => {
        return this.mdlaScope() === MDLA_SCOPE_ENUM.NAM;
    });

    protected isInternalUser = select(UsersState.isInternalUser());

    protected currentUrl = signal(this.router.url);

    protected isAdminPage: Signal<boolean> = computed(() => {
        return this.currentUrl().startsWith('/admin');
    });

    protected linkVisible: Signal<boolean> = computed(() => {
        return this.isNam() && this.isInternalUser() && !this.isAdminPage();
    });

    protected currentRoute: WritableSignal<string> = signal('');

    protected menuRoute: Signal<string> = computed(() => {
        const scope = this.mdlaScope();
        switch (scope) {
            case MDLA_SCOPE_ENUM.EMEA:
                return '/emea/menu';
            case MDLA_SCOPE_ENUM.CLW:
                return '/clw/menu';
            default:
                return '/nam/menu';
        }
    });

    public ngOnInit(): void {
        this.setTheme('dark');
        this.listenToMe();
        this.listenToRoutes();
        this.initAvailableLanguages();
    }

    public ngAfterViewInit(): void {
        effect(
            () => {
                this.showEnvironmentHint(); // reactive dependency
                requestAnimationFrame(() => {
                    const bottom = this.headerBar.nativeElement.getBoundingClientRect().bottom;
                    document.documentElement.style.setProperty('--header-height', `${bottom}px`);
                });
            },
            { injector: this.injector }
        );
    }

    private initAvailableLanguages(): void {
        if (this.currentRoute().startsWith('/admin')) {
            this.supportedLanguages.set(Object.values(SupportedLanguages));
            return;
        }

        switch (this.mdlaScope()) {
            case MDLA_SCOPE_ENUM.NAM:
            case MDLA_SCOPE_ENUM.CLW:
                this.supportedLanguages.set([SupportedLanguages.EN]);
                break;
            default:
                this.supportedLanguages.set(Object.values(SupportedLanguages));
                break;
        }
    }

    protected setTheme(mode: 'dark' | 'light'): void {
        this.themeService.setTheme(mode);
        localStorage.setItem('theme', JSON.stringify(mode));
    }

    private listenToMe(): void {
        this.me$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((me) => {
                    this.isTaskOwnerOrAdmin.set(
                        me.roles?.includes(Roles.TASK_OWNER) || me.roles?.includes(Roles.ADMIN) || false
                    );
                    // TODO: Add scope switching logic when EMEA_AGENT, NAM_AGENT, CLW_AGENT roles are implemented
                })
            )
            .subscribe();
    }

    private listenToRoutes(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => event.url),
                tap((url) => {
                    this.currentUrl.set(url);
                    this.currentRoute.set(url);
                    this.initAvailableLanguages();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
